/**
 * JsonShapePreview — JSON-flavored rendering of an inferred column shape.
 *
 * Driven by a flat `JsonStructureCachePath[]` list — either a fresh
 * `inferJsonStructureFromValues` result or a stored
 * `jsonStructureCache.paths` entry — so the dialog (live samples) and
 * MetaInfo manager (cached paths) share one renderer. Empty input gracefully
 * degrades to a muted "no structure inferred" line.
 *
 * When `enumCtx` is provided, each scalar-leaf or array row gains an inline
 * enum-state glyph next to its property name:
 *
 *   • declared (blue ▾)   — an `EnumColumnDeclaration` exists for the path.
 *                           Clicking opens the path-scoped enum details dialog.
 *   • eligible (gray ▾?)  — name passes `nameSuggestsEnum` (or `pickMode` is
 *                           on, see below). Clicking opens the declare dialog
 *                           pre-filled with the path + pathKind.
 *
 * When `enumCtx.pickMode === true`, the eligible glyph is rendered on *every*
 * scalar/array row regardless of name — that's the "Declare property as
 * enum…" pick-mode the structure-details dialog enters when the user clicks
 * the bottom-left footer link.
 */
import { useEffect, useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, FoldVertical, UnfoldVertical } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../../components/ui/button';
import {
  buildPathTree,
  jsonPercentFormatter,
  TYPE_LABELS,
  type PathTreeNode,
} from './filters/jsonPathTree';
import {
  openDeclareEnumColumnDialog,
  type DeclareEnumColumnDialogTrigger,
} from './filters/declareEnumColumnDialogStore';
import { openEnumDetailsDialog } from './meta-info/enumDetailsDialogStore';
import { openLooseRelationshipEditorDialog } from './filters/looseRelationshipEditorDialogStore';
import { PathEnumGlyph } from './meta-info/PathEnumGlyph';
import { PathLooseFkGlyph } from './meta-info/PathLooseFkGlyph';
import { nameSuggestsEnum } from '../utils/enumColumnCategorization';
import { findEnumDeclarationForPath } from '../utils/pathEnumLookup';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  EnumColumnDeclaration,
  JsonStructureCachePath,
  LooseRelationship,
} from '@/types/electron';

export type JsonShapeEnumCtx = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  enumColumns: ReadonlyArray<EnumColumnDeclaration>;
  /** When true, every scalar / array row gets the eligible glyph regardless
   *  of name — the structure-details dialog "Declare property as enum…"
   *  pick-mode. */
  pickMode?: boolean;
  /** Analytics trigger to attribute the ▾? click to. Defaults to
   *  `'metainfo-manager'` (the preview's original home in the MetaInfo
   *  Manager dialog). Callers reached from elsewhere — e.g.
   *  `JsonStructureDetailsDialog`, opened from a column-header right-click —
   *  should pass their own trigger so funnel attribution stays accurate. */
  trigger?: DeclareEnumColumnDialogTrigger;
};

/** Parallel to {@link JsonShapeEnumCtx} for the "Declare property as loose FK…"
 *  pick-mode in `JsonStructureDetailsDialog`. When `pickMode === true`, every
 *  scalar / array row becomes a click target that opens the loose-FK editor
 *  pre-filled with the picked JSONPath. Mutually exclusive with enum pickMode
 *  — the dialog only flips one on at a time. */
export type JsonShapeLooseFkCtx = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  /** Engine-typed column type of the JSON host column — threaded into the
   *  loose-FK editor's locked `source.dataType` field. */
  dataType: string;
  pickMode: boolean;
  /** All loose-FK declarations sourced from this column. The renderer picks
   *  the one matching each leaf's `sourcePath` to show a "declared" glyph. */
  looseRelationships?: ReadonlyArray<LooseRelationship>;
};

export type JsonShapePreviewProps = {
  paths: ReadonlyArray<JsonStructureCachePath>;
  /** Initial expansion depth. 1 = root keys expanded, children collapsed. */
  defaultExpandDepth?: number;
  className?: string;
  /** When set, render as a fixed-height scrollable viewport. */
  maxHeight?: string;
  /** When provided, enables inline enum-state glyphs next to property names.
   *  Absent → no glyphs (e.g. the read-only fallback in JsonStructureInspector
   *  without a live connection). */
  enumCtx?: JsonShapeEnumCtx;
  /** When set with `pickMode: true`, every scalar / array row becomes a click
   *  target that opens the loose-FK editor pre-filled with that JSONPath.
   *  Overrides any enum click handler while active. */
  looseFkCtx?: JsonShapeLooseFkCtx;
};

const INDENT_PX = 12;
type ContainerKind = 'object' | 'array' | 'leaf';

function containerKindOf(node: PathTreeNode): ContainerKind {
  if (node.children.length === 0) return 'leaf';
  // Wildcard-collapsed arrays carry their `[*]` suffix on the segment;
  // everything else with children is an object.
  return node.segment.endsWith(']') ? 'array' : 'object';
}

/** Strip trailing `[*]` from the rendered key name — the brackets show
 *  up as the array container's own decoration instead. */
function stripArraySuffix(segment: string): string {
  return segment.replace(/(\[\*\])+$/, '');
}

/** Short summary line for a collapsed container, e.g. `{ 3 keys }`,
 *  `[ string ]`, `[ { 4 keys } ]`. */
function collapsedSummary(node: PathTreeNode): string {
  const kind = containerKindOf(node);
  if (kind === 'object') {
    const n = node.children.length;
    return n === 1 ? '{ 1 key }' : `{ ${n} keys }`;
  }
  if (kind === 'array') {
    if (node.children.length > 0) {
      const child = node.children[0];
      if (containerKindOf(child) === 'object') {
        const n = child.children.length;
        return `[ ${n === 1 ? '{ 1 key }' : `{ ${n} keys }`} ]`;
      }
    }
    if (node.type && node.type !== 'array') return `[ ${TYPE_LABELS[node.type]} ]`;
    return '[ array ]';
  }
  return '';
}

type RowProps = {
  node: PathTreeNode;
  depth: number;
  expanded: ReadonlySet<string>;
  onToggle: (path: string) => void;
  isLast: boolean;
  enumCtx?: JsonShapeEnumCtx;
  looseFkCtx?: JsonShapeLooseFkCtx;
};

const SPACER = <span className="inline-block h-4 w-4 shrink-0" aria-hidden="true" />;

function ShapeRow({ node, depth, expanded, onToggle, isLast, enumCtx, looseFkCtx }: RowProps) {
  const kind = containerKindOf(node);
  const isContainer = kind !== 'leaf';
  const isOpen = expanded.has(node.path);
  const indentPx = depth * INDENT_PX;
  const prevalence =
    node.prevalence !== undefined ? jsonPercentFormatter.format(node.prevalence) : '';
  const open = kind === 'object' ? '{' : kind === 'array' ? '[' : '';
  const close = kind === 'object' ? '}' : kind === 'array' ? ']' : '';
  const trail = isLast ? '' : ',';
  const valueLabel = isContainer
    ? isOpen ? open : `${collapsedSummary(node)}${trail}`
    : `${node.type ? TYPE_LABELS[node.type] : ''}${trail}`;

  // Loose-FK pick mode overrides all enum interaction — when on, every
  // scalar / array row is a single click target that opens the loose-FK
  // editor pre-filled with the picked JSONPath. The dialog guarantees the
  // two pick modes are mutually exclusive, but we suppress enum glyphs here
  // defensively so a stray declared/eligible glyph can't steal the click.
  const isLooseFkPickMode = looseFkCtx?.pickMode === true;

  // Enum-state for inline glyph rendering. Only scalar leaves and array rows
  // can be enum targets — object containers are not enum-shaped. See the
  // file header for the declared/eligible/pickMode rules.
  const enumPathKind: 'scalar' | 'array' | null =
    kind === 'array' ? 'array' : kind === 'leaf' ? 'scalar' : null;
  const declared = !isLooseFkPickMode && enumCtx && enumPathKind
    ? findEnumDeclarationForPath(
        enumCtx.enumColumns,
        enumCtx.schema,
        enumCtx.table,
        enumCtx.column,
        node.path,
      )
    : undefined;
  const eligible = !isLooseFkPickMode && enumCtx && enumPathKind && !declared
    ? (enumCtx.pickMode === true || nameSuggestsEnum(stripArraySuffix(node.segment)))
    : false;

  // Loose-FK click handler — fires when the user is in loose-FK pick mode
  // and the row is scalar/array. Wins over the enum click handler.
  const looseFkClickHandler =
    isLooseFkPickMode && enumPathKind && looseFkCtx
      ? () =>
          openLooseRelationshipEditorDialog({
            connectionId: looseFkCtx.connectionId,
            database: looseFkCtx.database,
            engine: looseFkCtx.engine,
            source: {
              schema: looseFkCtx.schema,
              table: looseFkCtx.table,
              name: looseFkCtx.column,
              dataType: looseFkCtx.dataType,
            },
            initialSourcePath: node.path,
            source_label: 'json-details-dialog-pickmode',
          })
      : null;

  // Existing loose-FK declaration on this exact path. Matches when not in
  // pick-mode so the inline glyph shows the declared state; in pick-mode the
  // pickmode-dim glyph wins instead (declared paths are still candidates
  // for additional declarations on the same path is not supported, but the
  // pick-mode visual cue is consistent across all scalar/array rows).
  const declaredLooseFk =
    enumPathKind && looseFkCtx?.looseRelationships
      ? looseFkCtx.looseRelationships.find(
          (r) => (r.sourcePath ?? '') === node.path,
        )
      : undefined;

  // Shared handler for opening the declare dialog on an eligible row. The
  // inline `▾?` glyph and the property name itself both fire this — the
  // banner reads "Click any property below to declare it as an enum" and
  // the name is the natural place users actually click. Without the name
  // also being a click target, the dialog felt inert.
  const enumClickHandler =
    enumCtx && enumPathKind && !declared && eligible
      ? () =>
          openDeclareEnumColumnDialog({
            connectionId: enumCtx.connectionId,
            database: enumCtx.database,
            schema: enumCtx.schema,
            table: enumCtx.table,
            column: enumCtx.column,
            engine: enumCtx.engine,
            mode: 'eligible',
            trigger: enumCtx.trigger ?? 'metainfo-manager',
            path: node.path,
            pathKind: enumPathKind,
          })
      : null;

  // Loose-FK takes precedence; otherwise fall through to enum.
  const eligibleClickHandler = looseFkClickHandler ?? enumClickHandler;
  const eligibleAriaLabel = looseFkClickHandler
    ? `Declare loose FK on ${stripArraySuffix(node.segment)}`
    : `Declare ${stripArraySuffix(node.segment)} as enum`;

  return (
    <Fragment>
      <div
        className="flex items-center gap-1 py-px"
        style={{ paddingLeft: indentPx }}
        data-testid={`json-shape-row-${node.path}`}
      >
        {isContainer ? (
          <button
            type="button"
            onClick={() => onToggle(node.path)}
            className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={isOpen ? `Collapse ${node.path}` : `Expand ${node.path}`}
            aria-expanded={isOpen}
            data-testid={`json-shape-toggle-${node.path}`}
          >
            {isOpen ? (
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
            )}
          </button>
        ) : SPACER}
        <span className="flex-1 min-w-0 flex items-baseline gap-1.5">
          {eligibleClickHandler ? (
            <button
              type="button"
              onClick={eligibleClickHandler}
              translate="no"
              className="text-blue-600 dark:text-blue-400 truncate cursor-pointer rounded-sm px-1 -mx-1 hover:bg-blue-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring text-left"
              data-testid={`json-shape-name-eligible-${node.path}`}
              aria-label={eligibleAriaLabel}
            >
              &quot;{stripArraySuffix(node.segment)}&quot;:
            </button>
          ) : (
            <span translate="no" className="text-blue-600 dark:text-blue-400 truncate">
              &quot;{stripArraySuffix(node.segment)}&quot;:
            </span>
          )}
          {enumCtx && enumPathKind && declared && (
            <PathEnumGlyph
              state="declared"
              path={node.path}
              onClick={() =>
                openEnumDetailsDialog({
                  connectionId: enumCtx.connectionId,
                  database: enumCtx.database,
                  schema: enumCtx.schema,
                  table: enumCtx.table,
                  column: enumCtx.column,
                  engine: enumCtx.engine,
                  declaredSource: declared.source,
                  source: 'json-shape-preview',
                  path: declared.path ?? node.path,
                  pathKind: declared.pathKind ?? enumPathKind,
                })
              }
            />
          )}
          {eligibleClickHandler && (
            <PathEnumGlyph
              state="eligible"
              path={node.path}
              onClick={eligibleClickHandler}
            />
          )}
          {looseFkCtx && enumPathKind && declaredLooseFk && !isLooseFkPickMode && (
            <PathLooseFkGlyph
              state="declared"
              path={node.path}
              onClick={() =>
                openLooseRelationshipEditorDialog({
                  connectionId: looseFkCtx.connectionId,
                  database: looseFkCtx.database,
                  engine: looseFkCtx.engine,
                  source: {
                    schema: looseFkCtx.schema,
                    table: looseFkCtx.table,
                    name: looseFkCtx.column,
                    dataType: looseFkCtx.dataType,
                  },
                  existingRelationshipId: declaredLooseFk.id,
                  initialSourcePath: declaredLooseFk.sourcePath ?? node.path,
                  source_label: 'json-details-dialog-glyph',
                })
              }
            />
          )}
          {looseFkCtx && enumPathKind && isLooseFkPickMode && !declaredLooseFk && (
            <PathLooseFkGlyph state="pickmode" path={node.path} />
          )}
          <span className="text-muted-foreground">{valueLabel}</span>
        </span>
        <span
          className="tabular-nums text-[10px] text-muted-foreground shrink-0 min-w-[2.5rem] text-right"
          aria-label={node.prevalence !== undefined ? `prevalence ${prevalence}` : undefined}
        >
          {prevalence}
        </span>
      </div>
      {isContainer && isOpen && (
        <>
          {node.children.map((child, idx) => (
            <ShapeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              isLast={idx === node.children.length - 1}
              enumCtx={enumCtx}
              looseFkCtx={looseFkCtx}
            />
          ))}
          <div className="flex items-center py-px" style={{ paddingLeft: indentPx }}>
            {SPACER}
            <span className="text-muted-foreground">{close}{trail}</span>
          </div>
        </>
      )}
    </Fragment>
  );
}

/** Collect every container path (node with children) in the shape tree.
 *  Drives both the Expand-all toolbar action and whether the toolbar
 *  shows at all (no containers ⇒ nothing useful to do). */
function collectShapeContainerPaths(root: PathTreeNode): string[] {
  const out: string[] = [];
  const walk = (n: PathTreeNode) => {
    if (n.children.length === 0) return;
    if (n.path !== '$') out.push(n.path);
    for (const c of n.children) walk(c);
  };
  for (const c of root.children) walk(c);
  return out;
}

export function JsonShapePreview({
  paths,
  defaultExpandDepth = 1,
  className,
  maxHeight,
  enumCtx,
  looseFkCtx,
}: JsonShapePreviewProps) {
  const tree = useMemo(() => buildPathTree(paths), [paths]);

  const allContainerPaths = useMemo(() => collectShapeContainerPaths(tree), [tree]);

  const initialExpanded = useMemo(() => {
    const set = new Set<string>();
    const walk = (node: PathTreeNode, depth: number) => {
      if (depth < defaultExpandDepth && node.children.length > 0) {
        set.add(node.path);
        for (const c of node.children) walk(c, depth + 1);
      }
    };
    for (const c of tree.children) walk(c, 0);
    return set;
  }, [tree, defaultExpandDepth]);

  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);
  useEffect(() => setExpanded(initialExpanded), [initialExpanded]);

  const toggle = (path: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const expandAll = () => setExpanded(new Set(allContainerPaths));
  const collapseAll = () => setExpanded(new Set());
  const hasExpandable = allContainerPaths.length > 0;

  if (paths.length === 0 || tree.children.length === 0) {
    return (
      <div
        className={cn(
          'text-xs text-muted-foreground italic font-mono',
          className,
        )}
        data-testid="json-shape-empty"
      >
        No JSON structure inferred.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative font-mono text-xs rounded-md border bg-muted/30 p-2',
        maxHeight && 'overflow-auto',
        className,
      )}
      style={maxHeight ? { maxHeight } : undefined}
      data-testid="json-shape-preview"
    >
      <div className="text-muted-foreground">{'{'}</div>
      {tree.children.map((child, idx) => (
        <ShapeRow
          key={child.path}
          node={child}
          depth={0}
          expanded={expanded}
          onToggle={toggle}
          isLast={idx === tree.children.length - 1}
          enumCtx={enumCtx}
          looseFkCtx={looseFkCtx}
        />
      ))}
      <div className="text-muted-foreground">{'}'}</div>
      {hasExpandable && (
        <div
          className="absolute top-1 right-2 flex gap-0.5 z-10 pointer-events-none"
          data-testid="json-shape-toolbar"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground pointer-events-auto bg-background/70 backdrop-blur-[2px] hover:bg-background"
            onClick={expandAll}
            aria-label="Expand all"
            title="Expand all"
            data-testid="json-shape-expand-all"
          >
            <UnfoldVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground pointer-events-auto bg-background/70 backdrop-blur-[2px] hover:bg-background"
            onClick={collapseAll}
            aria-label="Collapse all"
            title="Collapse all"
            data-testid="json-shape-collapse-all"
          >
            <FoldVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}
